import React, { useCallback, useEffect, useMemo, useState } from "react";
import { QueryService } from "../../../services/QueryService";
import {
  AfishaApiEvent,
  AfishaEvent,
  buildDaysRange,
  getTodayDateStr,
  mapApiEventsToDayEvents,
  mapApiEventsToEventsByDate,
} from "./afishaUtils";
import { AfishaTrack } from "./AfishaTrack";
import { AfishaTagsPicker, AfishaTag } from "./AfishaTagsPicker";
import { AfishaFilterDays } from "./AfishaFilterDays";
import {
  markAfishaBootSuccess,
  reportAfishaLoadError,
} from "../../../services/clientAlert";
import {
  useAfishaHomeSearchBridge,
  useRegisterAfishaHomeSearch,
} from "./afishaHomeSearchBridge";
import "./AfishaPage.scss";

type AfishaInitResponse = {
  type: string;
  data: {
    activeDate: string;
    events: AfishaApiEvent[];
  };
};

type AfishaDayResponse = {
  type: string;
  data: {
    date: string;
    events: AfishaApiEvent[];
  };
};

type AfishaTagsResponse = {
  type: string;
  data: AfishaTag[];
};

type AfishaSearchResponse = {
  type: string;
  data: {
    query: string;
    events: AfishaApiEvent[];
  };
};

export const AfishaHome = () => {
  const { setSearchPanelOpen } = useAfishaHomeSearchBridge();
  const dayMeta = useMemo(() => buildDaysRange(10), []);
  const dayDates = useMemo(() => dayMeta.map((day) => day.date), [dayMeta]);
  const [activeDate, setActiveDate] = useState(getTodayDateStr());
  const [eventsByDate, setEventsByDate] = useState<Record<string, AfishaEvent[]>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [error, setError] = useState("");

  const [filterOpen, setFilterOpen] = useState(false);
  const [searchBarKey, setSearchBarKey] = useState(0);
  const [tags, setTags] = useState<AfishaTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [filterEventsByDate, setFilterEventsByDate] = useState<
    Record<string, AfishaEvent[]>
  >({});
  const [filterLoading, setFilterLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const loadDayEvents = useCallback(async (date: string) => {
    setLoadingDay(true);
    try {
      const resp = await QueryService.postAfishaQuery("/api/events/afisha", {
        date,
      });
      const payload = resp?.data as AfishaDayResponse | undefined;
      const events = mapApiEventsToDayEvents(payload?.data?.events ?? []);
      setEventsByDate((prev) => ({ ...prev, [date]: events }));
    } catch {
      setError("Не удалось загрузить события");
    } finally {
      setLoadingDay(false);
    }
  }, []);

  const loadTags = useCallback(async () => {
    if (tags.length) return;
    setTagsLoading(true);
    try {
      const resp = await QueryService.postAfishaQuery("/api/events/afisha/tags");
      const payload = resp?.data as AfishaTagsResponse | undefined;
      setTags(payload?.data ?? []);
    } catch {
      setError("Не удалось загрузить категории");
    } finally {
      setTagsLoading(false);
    }
  }, [tags.length]);

  const loadFilteredEvents = 
    async (tagId: number) => {
      setFilterLoading(true);
      setError("");
      try {
        const results = await Promise.all(
          dayMeta.map((day) =>
            QueryService.postAfishaQuery("/api/events/afisha", {
              date: day.date,
              tagId,
            }),
          ),
        );

        const byDate: Record<string, AfishaEvent[]> = {};
        dayMeta.forEach((day, index) => {
          const payload = results[index]?.data as AfishaDayResponse | undefined;
          byDate[day.date] = mapApiEventsToDayEvents(
            payload?.data?.events ?? [],
          );
        });
        setFilterEventsByDate(byDate);
      } catch {
        setError("Не удалось загрузить события по фильтру");
      } finally {
        setFilterLoading(false);
      }
    }

  const loadSearchResults = useCallback(
    async (query: string) => {
      setSearchLoading(true);
      setError("");
      try {
        const resp = await QueryService.postAfishaQuery("/api/events/afisha/search", {
          query,
        });
        const payload = resp?.data as AfishaSearchResponse | undefined;
        const events = payload?.data?.events ?? [];
        setFilterEventsByDate(mapApiEventsToEventsByDate(events, dayDates));
      } catch {
        setError("Не удалось выполнить поиск");
      } finally {
        setSearchLoading(false);
      }
    },
    [dayDates],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setEventsByDate({});
      setActiveDate(getTodayDateStr());

      try {
        const resp = await QueryService.postAfishaQuery("/api/events/afisha");
        const payload = resp?.data as AfishaInitResponse | undefined;
        if (cancelled) return;

        const today = payload?.data?.activeDate ?? getTodayDateStr();
        const initialEvents = mapApiEventsToDayEvents(
          payload?.data?.events ?? [],
        );
        setActiveDate(today);
        setEventsByDate({ [today]: initialEvents });
      } catch {
        if (!cancelled) {
          const loadError = "Не удалось загрузить афишу";
          setError(loadError);
          reportAfishaLoadError(loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loading && !error) {
      markAfishaBootSuccess();
    }
  }, [loading, error]);

  const openFilter = async () => {
    setFilterOpen(true);
    await loadTags();
  };

  const closeFilter = () => {
    setFilterOpen(false);
    setSelectedTagId(null);
    setFilterEventsByDate({});
  };

  const resetSearchField = useCallback(() => {
    setSearchQuery("");
    setFilterEventsByDate({});
    setSearchBarKey((k) => k + 1);
  }, []);

  const toggleFilter = () => {
    if (filterOpen) {
      closeFilter();
      return;
    }
    resetSearchField();
    setSearchPanelOpen(false);
    void openFilter();
  };

  const handleTagSelect = async (tagId: number) => {
    setSelectedTagId(tagId);
    setSearchQuery("");
    setSearchBarKey((k) => k + 1);
    await loadFilteredEvents(tagId);
  };

  const handleSearch = useCallback(
    async (query: string) => {
      setFilterOpen(false);
      setSelectedTagId(null);
      setSearchQuery(query);
      await loadSearchResults(query);
    },
    [loadSearchResults],
  );

  const homeSearchBridge = useMemo(
    () => ({
      onSearch: handleSearch,
      clearSearch: resetSearchField,
      searchLoading,
      resetKey: searchBarKey,
    }),
    [handleSearch, resetSearchField, searchLoading, searchBarKey],
  );

  useRegisterAfishaHomeSearch(homeSearchBridge);

  const handleDateClick = async (date: string) => {
    setActiveDate(date);
    if (eventsByDate[date] !== undefined) return;
    await loadDayEvents(date);
  };

  const events = eventsByDate[activeDate];
  const isDayLoading = loadingDay && events === undefined;
  const showMultiDayResults =
    Boolean(searchQuery) || (filterOpen && selectedTagId !== null);
  const filterResultsLoading = filterLoading || searchLoading;
  const hasFilterResults = selectedTagId !== null || Boolean(searchQuery);
  const showDayStrip = !showMultiDayResults;

  return (
    <>
      {!loading && !error && (
        <div className="afisha__toolbar">
          {showDayStrip && (
            <div className="afisha__dates" role="tablist" aria-label="Даты">
              {dayMeta.map((day) => {
                const isActive = day.date === activeDate;
                return (
                  <button
                    key={day.date}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={
                      "afisha__date" + (isActive ? " afisha__date--active" : "")
                    }
                    onClick={() => handleDateClick(day.date)}
                  >
                    <span className="afisha__date-weekday">{day.weekday}</span>
                    <span className="afisha__date-num">{day.dayNum}</span>
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            className={
              "afisha__toolbar-btn" +
              (filterOpen ? " afisha__toolbar-btn--active" : "")
            }
            aria-pressed={filterOpen}
            aria-label={filterOpen ? "Закрыть фильтр" : "Фильтр"}
            title={filterOpen ? "Закрыть фильтр" : "Фильтр"}
            onClick={() => void toggleFilter()}
          >
            <i
              className={filterOpen ? "bi bi-x-lg" : "bi bi-funnel"}
              aria-hidden
            />
          </button>
        </div>
      )}

      {filterOpen && !loading && !error && (
        <AfishaTagsPicker
          tags={tags}
          selectedTagId={selectedTagId}
          loading={tagsLoading}
          onSelect={handleTagSelect}
        />
      )}

      {loading && <p className="afisha__status">Загрузка…</p>}
      {error && <p className="afisha__status afisha__status--error">{error}</p>}

      {!loading && !error && !showMultiDayResults && (
        <div className="afisha__tracks" aria-live="polite">
          {isDayLoading ? (
            <p className="afisha__status">Загрузка…</p>
          ) : events && events.length > 0 ? (
            events.map((event) => (
              <AfishaTrack key={`${activeDate}-${event.id}`} event={event} />
            ))
          ) : events ? (
            <p className="afisha__empty">На этот день событий пока нет</p>
          ) : null}
        </div>
      )}

      {!loading && !error && showMultiDayResults && (
        <div className="afisha__tracks afisha__tracks--filter" aria-live="polite">
          {!hasFilterResults ? null : (
            <AfishaFilterDays
              days={dayMeta}
              eventsByDate={filterEventsByDate}
              loading={filterResultsLoading}
              emptyText={
                searchQuery
                  ? `По запросу «${searchQuery}» ничего не найдено`
                  : "В выбранной категории нет событий на ближайшие дни"
              }
            />
          )}
        </div>
      )}
    </>
  );
};
